"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  Trash2,
  Plus,
  Loader2,
  AlertCircle,
  Camera,
} from "lucide-react";
import { FormGroup } from "../components/ui/form-group";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Dropdown, DropdownItem } from "../components/ui/dropdown";
import {
  TIME_OPTIONS,
  loadSettingsData,
  updateVeterinarySettings,
  getAvailableTimeOptions,
  validateTimeSlots,
  convertTime12ToMinutes,
  convertMinutesToTime12,
} from "../utils/settingsUtils";

const generateId = () => Math.random().toString(36).substr(2, 9);

const TimePicker = ({
  value,
  onChange,
  placeholder = "Select time",
  disabled = false,
  error = false,
  availableTimes = TIME_OPTIONS,
}) => {
  const dropdownTrigger = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        border: `1px solid ${error ? "#EF4444" : disabled ? "#D1D5DB" : "#D1D5DB"}`,
        borderRadius: "8px",
        backgroundColor: disabled ? "#F3F4F6" : "#FFFFFF",
        cursor: disabled ? "not-allowed" : "pointer",
        width: "120px",
        fontSize: "14px",
        color: value ? "#374151" : "#9CA3AF",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span>{value || placeholder}</span>
      <ChevronDown size={16} color={disabled ? "#9CA3AF" : "#6B7280"} />
    </div>
  );

  return (
    <Dropdown
      trigger={dropdownTrigger}
      minWidth="120px"
      align="left"
      maxHeight="240px"
      disabled={disabled}
    >
      {availableTimes.map((time) => (
        <DropdownItem
          key={time.value24}
          onClick={() => onChange(time.display)}
          variant={value === time.display ? "selected" : "default"}
          style={{
            textAlign: "center",
            justifyContent: "center",
          }}
          disabled={false}
        >
          {time.display}
        </DropdownItem>
      ))}
    </Dropdown>
  );
};

export default function SettingsScreen() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    contact: "",
    officeAddress: "",
    bio: "",
    currency: "USD",
    timeslotDuration: "30 minutes",
    photoUrl: null,
    availability: {
      Mon: { enabled: false, slots: [] },
      Tue: { enabled: false, slots: [] },
      Wed: { enabled: false, slots: [] },
      Thu: { enabled: false, slots: [] },
      Fri: { enabled: false, slots: [] },
      Sat: { enabled: false, slots: [] },
      Sun: { enabled: false, slots: [] },
    },
  });
  const [initialData, setInitialData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [timeslotOptions] = useState([
    "15 minutes",
    "30 minutes",
    "45 minutes",
    "60 minutes",
    "90 minutes",
    "120 minutes",
  ]);
  const [slotValidation, setSlotValidation] = useState({});
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [deletePhoto, setDeletePhoto] = useState(false);

  const fileInputRef = useRef(null);

  const maxBioLength = 400;
  const charactersLeft = maxBioLength - formData.bio.length;

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    return () => {
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const { formData: loadedData, currencyOptions: loadedCurrencies } =
        await loadSettingsData();
      console.log(loadedData);

      setFormData(loadedData);
      setCurrencyOptions(loadedCurrencies);
      setInitialData(JSON.parse(JSON.stringify(loadedData)));
      setSlotValidation({});
      setPhotoPreview(loadedData.photoUrl);
      setSelectedPhoto(null);
      setDeletePhoto(false);
    } catch (error) {
      alert("Failed to load settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const newSlotValidation = {};
    Object.entries(formData.availability).forEach(([day, dayData]) => {
      if (dayData.enabled && dayData.slots.length > 0) {
        const validation = validateTimeSlots(dayData.slots);
        if (!validation.isValid) {
          newSlotValidation[day] = validation.conflicts;
        }
      }
    });
    setSlotValidation(newSlotValidation);
  }, [formData.availability]);

  useEffect(() => {
    if (initialData) {
      const dataToCompare = {
        ...formData,
        photoUrl: deletePhoto ? null : photoPreview || formData.photoUrl,
      };
      const initialToCompare = {
        ...initialData,
        photoUrl: initialData.photoUrl,
      };
      const changed =
        JSON.stringify(dataToCompare) !== JSON.stringify(initialToCompare);
      setHasChanges(changed);
    }
  }, [formData, initialData, photoPreview, deletePhoto]);

  const hasValidationErrors = () => {
    return Object.keys(slotValidation).length > 0;
  };

  const toggleDay = (day) => {
    const newEnabled = !formData.availability[day].enabled;
    const newSlots = newEnabled
      ? [
          {
            id: generateId(),
            start: "09:00 AM",
            end: "05:00 PM",
          },
        ]
      : [];

    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          enabled: newEnabled,
          slots: newSlots,
        },
      },
    }));

    setSlotValidation((prev) => {
      const newErrors = { ...prev };
      delete newErrors[day];
      return newErrors;
    });
  };

  const addTimeSlot = (day) => {
    const currentSlots = formData.availability[day].slots;
    let newStartTime = "09:00 AM";
    let newEndTime = "05:00 PM";

    if (currentSlots.length > 0) {
      const sortedSlots = [...currentSlots].sort(
        (a, b) =>
          convertTime12ToMinutes(a.start) - convertTime12ToMinutes(b.start),
      );

      const lastSlot = sortedSlots[sortedSlots.length - 1];
      const lastEndMinutes = convertTime12ToMinutes(lastSlot.end);

      if (lastEndMinutes + 60 <= 1440) {
        newStartTime = convertMinutesToTime12(lastEndMinutes + 60);
        newEndTime = convertMinutesToTime12(lastEndMinutes + 240);
      }
    }

    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          enabled: true,
          slots: [
            ...prev.availability[day].slots,
            {
              id: generateId(),
              start: newStartTime,
              end: newEndTime,
            },
          ],
        },
      },
    }));
  };

  const removeTimeSlot = (day, slotId) => {
    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          slots: prev.availability[day].slots.filter(
            (slot) => slot.id !== slotId,
          ),
        },
      },
    }));

    if (slotValidation[day]) {
      const newErrors = slotValidation[day].filter(
        (error) =>
          error.slotId !== slotId &&
          (!error.otherSlotId || error.otherSlotId !== slotId),
      );

      setSlotValidation((prev) => ({
        ...prev,
        [day]: newErrors.length > 0 ? newErrors : undefined,
      }));
    }
  };

  const updateTimeSlot = (day, slotId, field, value) => {
    const currentSlots = formData.availability[day].slots;
    const currentSlot = currentSlots.find((slot) => slot.id === slotId);

    if (currentSlot && currentSlot[field] === value) return;

    if (field === "start" && currentSlot) {
      const newStartMinutes = convertTime12ToMinutes(value);
      const currentEndMinutes = convertTime12ToMinutes(currentSlot.end);

      if (newStartMinutes >= currentEndMinutes) {
        const newEndTime = convertMinutesToTime12(newStartMinutes + 60);

        setFormData((prev) => ({
          ...prev,
          availability: {
            ...prev.availability,
            [day]: {
              ...prev.availability[day],
              slots: prev.availability[day].slots.map((slot) =>
                slot.id === slotId
                  ? {
                      ...slot,
                      [field]: value,
                      end: newEndTime,
                    }
                  : slot,
              ),
            },
          },
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          slots: prev.availability[day].slots.map((slot) =>
            slot.id === slotId ? { ...slot, [field]: value } : slot,
          ),
        },
      },
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBioChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      bio: value.slice(0, maxBioLength),
    }));
  };

  const handleCurrencyChange = (currencyCode) => {
    setFormData((prev) => ({
      ...prev,
      currency: currencyCode.toUpperCase(),
    }));
  };

  const handlePhotoSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedPhoto(file);
      setDeletePhoto(false);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
    }
  };

  const handleDeletePhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
    setDeletePhoto(true);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    try {
      if (hasValidationErrors()) {
        alert("Please fix time slot validation errors before saving.");
        return;
      }

      setSaving(true);

      const response = await updateVeterinarySettings(
        formData,
        selectedPhoto,
        deletePhoto,
      );

      alert(response.message || "Settings saved successfully!");
      await loadInitialData();
    } catch (error) {
      alert(error.message || "Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (
        window.confirm(
          "Are you sure you want to cancel? Any unsaved changes will be lost.",
        )
      ) {
        setFormData(initialData);
        setPhotoPreview(initialData?.photoUrl || null);
        setSelectedPhoto(null);
        setDeletePhoto(false);
        setHasChanges(false);
        setSlotValidation({});

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const getCurrentCurrencyDisplay = () => {
    const currency = currencyOptions.find(
      (c) => c.code === formData.currency.toUpperCase(),
    );
    return currency
      ? currency.display
      : `${formData.currency.toUpperCase()} - Select currency`;
  };

  const getSlotError = (day, slotId) => {
    return slotValidation[day]?.find(
      (error) => error.slotId === slotId || error.otherSlotId === slotId,
    );
  };

  const getAvailableTimesForSlot = (day, slotId, field) => {
    const currentSlots = formData.availability[day].slots;
    const currentSlot = currentSlots.find((slot) => slot.id === slotId);

    if (!currentSlot) return TIME_OPTIONS;

    return getAvailableTimeOptions(
      currentSlots,
      slotId,
      field,
      currentSlot[field],
    );
  };

  const styles = {
    container: {
      minHeight: "100vh",
      padding: "32px 48px",
      fontFamily: "system-ui, -apple-system, sans-serif",
    },
    title: {
      fontSize: "28px",
      fontWeight: 600,
      color: "#1a1a2e",
      marginBottom: "32px",
    },
    card: {
      backgroundColor: "#FFFFFF",
      borderRadius: "12px",
      border: "1px solid #E5E7EB",
      padding: "32px",
      maxWidth: "1000px",
      margin: "0 auto",
    },
    sectionTitle: {
      fontSize: "16px",
      fontWeight: 600,
      color: "#1a1a2e",
      marginBottom: "4px",
    },
    sectionSubtitle: {
      fontSize: "14px",
      color: "#6B7280",
      marginBottom: "24px",
    },
    divider: {
      height: "1px",
      backgroundColor: "#E5E7EB",
      margin: "24px 0",
    },
    formRow: {
      display: "flex",
      alignItems: "flex-start",
      marginBottom: "24px",
      gap: "24px",
      flexWrap: "wrap",
    },
    labelColumn: {
      width: "200px",
      flexShrink: 0,
      minWidth: "200px",
    },
    labelText: {
      fontSize: "14px",
      fontWeight: 500,
      color: "#374151",
      marginBottom: "4px",
    },
    labelHint: {
      fontSize: "13px",
      color: "#6B7280",
    },
    inputWrapper: {
      flex: 1,
      minWidth: "300px",
      maxWidth: "500px",
    },
    photoSection: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "16px",
    },
    avatarWrapper: {
      position: "relative",
    },
    avatar: {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      objectFit: "cover",
      border: "2px solid #E5E7EB",
    },
    avatarPlaceholder: {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      backgroundColor: "#F3F4F6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "2px solid #E5E7EB",
    },
    photoActions: {
      display: "flex",
      gap: "16px",
      flexWrap: "wrap",
    },
    availabilitySubtitle: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#374151",
      marginBottom: "4px",
    },
    availabilityHint: {
      fontSize: "13px",
      color: "#6B7280",
      marginBottom: "20px",
      lineHeight: 1.5,
    },
    dayRow: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      marginBottom: "12px",
      minHeight: "44px",
      flexWrap: "wrap",
    },
    checkbox: {
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      border: "2px solid #D1D5DB",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
      transition: "all 0.2s",
    },
    checkboxChecked: {
      backgroundColor: "#F97316",
      borderColor: "#F97316",
    },
    dayLabel: {
      width: "40px",
      fontSize: "14px",
      color: "#374151",
      fontWeight: 500,
      flexShrink: 0,
    },
    unavailableText: {
      fontSize: "14px",
      color: "#9CA3AF",
      fontStyle: "italic",
      marginLeft: "8px",
    },
    iconButton: {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      border: "none",
      background: "none",
      color: "#F97316",
      transition: "background-color 0.2s",
      marginLeft: "8px",
      flexShrink: 0,
    },
    iconButtonDisabled: {
      opacity: 0.5,
      cursor: "not-allowed",
    },
    footer: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      marginTop: "32px",
      paddingTop: "24px",
      borderTop: "1px solid #E5E7EB",
    },
    dropdownTrigger: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 14px",
      border: "1px solid #D1D5DB",
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      cursor: "pointer",
      width: "100%",
      maxWidth: "300px",
      fontSize: "14px",
      color: "#374151",
      justifyContent: "space-between",
    },
    durationDropdownTrigger: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 14px",
      border: "1px solid #D1D5DB",
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      cursor: "pointer",
      width: "200px",
      fontSize: "14px",
      color: "#374151",
    },
    charCount: {
      fontSize: "13px",
      color: charactersLeft < 20 ? "#EF4444" : "#6B7280",
      marginTop: "8px",
      textAlign: "right",
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      backgroundColor: "#FAFAFA",
    },
    loadingText: {
      marginTop: "16px",
      color: "#6B7280",
      fontSize: "16px",
    },
    errorContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      backgroundColor: "#FAFAFA",
      padding: "20px",
      textAlign: "center",
    },
    errorTitle: {
      fontSize: "24px",
      fontWeight: 600,
      color: "#DC2626",
      marginBottom: "16px",
    },
    errorMessage: {
      fontSize: "16px",
      color: "#6B7280",
      marginBottom: "24px",
      maxWidth: "500px",
    },
    timeSlotGroup: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginLeft: "60px",
      flexWrap: "wrap",
    },
    timeSlotInputs: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexWrap: "wrap",
    },
    validationError: {
      fontSize: "12px",
      color: "#EF4444",
      marginTop: "4px",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    validationWarning: {
      fontSize: "14px",
      color: "#EF4444",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "12px",
      backgroundColor: "#FEF2F2",
      borderRadius: "8px",
      marginBottom: "16px",
    },
    timeSlotContainer: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    timeSlotRow: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    hiddenInput: {
      display: "none",
    },
    photoFileName: {
      fontSize: "13px",
      color: "#6B7280",
      marginTop: "4px",
    },
  };

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <Loader2
          size={48}
          color="#F97316"
          style={{ animation: "spin 1s linear infinite" }}
        />
        <p style={styles.loadingText}>Loading settings...</p>
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (!formData.email && !loading) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorTitle}>No Settings Found</h2>
        <p style={styles.errorMessage}>
          Unable to load veterinary settings. Please make sure you are logged in
          as a veterinarian and your profile is properly set up.
        </p>
        <Button variant="primary" onClick={loadInitialData}>
          Retry
        </Button>
      </div>
    );
  }

  const displayPhoto = deletePhoto ? null : photoPreview || formData.photoUrl;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Settings</h1>

      <div style={styles.card}>
        <div>
          <h2 style={styles.sectionTitle}>Profile</h2>
          <p style={styles.sectionSubtitle}>
            Update your photo and personal details here.
          </p>
        </div>

        <div style={styles.divider} />

        <div style={styles.formRow}>
          <div style={styles.labelColumn}>
            <div style={styles.labelText}>Your photo</div>
            <div style={styles.labelHint}>
              This will be displayed on your profile.
            </div>
          </div>
          <div
            style={{
              ...styles.inputWrapper,
              display: "flex",
              flexDirection: "column",
              maxWidth: "none",
              flex: 1,
            }}
          >
            <div style={styles.photoSection}>
              <div style={styles.avatarWrapper}>
                {displayPhoto ? (
                  <img src={displayPhoto} alt="Profile" style={styles.avatar} />
                ) : (
                  <div style={styles.avatarPlaceholder}>
                    <Camera size={32} color="#9CA3AF" />
                  </div>
                )}
              </div>
              <div style={styles.photoActions}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  style={styles.hiddenInput}
                />
                <Button
                  variant="ghost"
                  onClick={handleDeletePhoto}
                  disabled={!displayPhoto && !deletePhoto}
                >
                  Delete
                </Button>
                <Button variant="outline" onClick={triggerFileInput}>
                  {displayPhoto ? "Update" : "Upload"}
                </Button>
              </div>
            </div>
            {selectedPhoto && (
              <div style={styles.photoFileName}>
                Selected: {selectedPhoto.name}
              </div>
            )}
            {deletePhoto && (
              <div style={{ ...styles.photoFileName, color: "#EF4444" }}>
                Photo will be deleted when you save
              </div>
            )}
          </div>
        </div>

        <div style={styles.divider} />

        <FormGroup
          label="Full Name"
          labelPosition="left"
          labelWidth="200px"
          marginBottom="24px"
        >
          <div style={{ maxWidth: "500px" }}>
            <Input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleInputChange("fullName", e.target.value)}
            />
          </div>
        </FormGroup>

        <FormGroup
          label="Email"
          labelPosition="left"
          labelWidth="200px"
          marginBottom="24px"
        >
          <div style={{ maxWidth: "500px" }}>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              disabled
            />
          </div>
        </FormGroup>

        <FormGroup
          label="Contact"
          labelPosition="left"
          labelWidth="200px"
          marginBottom="24px"
        >
          <div style={{ maxWidth: "500px" }}>
            <Input
              type="tel"
              value={formData.contact}
              onChange={(e) => handleInputChange("contact", e.target.value)}
            />
          </div>
        </FormGroup>

        <FormGroup
          label="Office Address"
          labelPosition="left"
          labelWidth="200px"
          marginBottom="24px"
        >
          <div style={{ maxWidth: "500px" }}>
            <Input
              type="text"
              value={formData.officeAddress}
              onChange={(e) =>
                handleInputChange("officeAddress", e.target.value)
              }
            />
          </div>
        </FormGroup>

        <FormGroup
          label="Your bio"
          hint="Write a short introduction."
          labelPosition="left"
          labelWidth="200px"
          marginBottom="24px"
        >
          <div style={{ maxWidth: "500px" }}>
            <Textarea
              value={formData.bio}
              onChange={(e) => handleBioChange(e.target.value)}
              rows={5}
            />
            <div style={styles.charCount}>{charactersLeft} characters left</div>
          </div>
        </FormGroup>

        <div style={styles.divider} />

        <FormGroup
          label="Currency"
          hint="Select your preferred currency for payments."
          labelPosition="left"
          labelWidth="200px"
          marginBottom="24px"
        >
          <div style={{ maxWidth: "300px" }}>
            <Dropdown
              trigger={
                <div style={styles.dropdownTrigger}>
                  <span>{getCurrentCurrencyDisplay()}</span>
                  <ChevronDown size={18} color="#6B7280" />
                </div>
              }
              minWidth="300px"
              maxHeight="300px"
            >
              {currencyOptions.map((currency) => (
                <DropdownItem
                  key={currency.code}
                  onClick={() => handleCurrencyChange(currency.code)}
                  variant={
                    currency.code === formData.currency.toUpperCase()
                      ? "selected"
                      : "default"
                  }
                >
                  {currency.display}
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
        </FormGroup>

        <div style={styles.divider} />

        <div style={styles.formRow}>
          <div style={styles.labelColumn}>
            <div style={styles.labelText}>Set Availability</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={styles.availabilitySubtitle}>
              Available times weekly
            </div>
            <div style={styles.availabilityHint}>
              Set your availability according to days of the week. Time slots
              cannot overlap.
            </div>

            {hasValidationErrors() && (
              <div style={styles.validationWarning}>
                <AlertCircle size={18} />
                <span>
                  Some time slots have validation errors. Please fix them before
                  saving.
                </span>
              </div>
            )}

            {days.map((day) => (
              <div key={day}>
                {formData.availability[day].enabled &&
                formData.availability[day].slots.length > 0 ? (
                  <div style={{ marginBottom: "12px" }}>
                    {formData.availability[day].slots.map((slot, index) => {
                      const slotError = getSlotError(day, slot.id);
                      const hasError = !!slotError;

                      return (
                        <div
                          key={slot.id}
                          style={{
                            ...styles.timeSlotContainer,
                            marginBottom:
                              index <
                              formData.availability[day].slots.length - 1
                                ? "16px"
                                : "12px",
                          }}
                        >
                          <div
                            style={{
                              ...styles.dayRow,
                              marginBottom: "8px",
                            }}
                          >
                            {index === 0 ? (
                              <>
                                <div
                                  style={{
                                    ...styles.checkbox,
                                    ...(formData.availability[day].enabled
                                      ? styles.checkboxChecked
                                      : {}),
                                  }}
                                  onClick={() => toggleDay(day)}
                                >
                                  {formData.availability[day].enabled && (
                                    <svg
                                      width="12"
                                      height="10"
                                      viewBox="0 0 12 10"
                                      fill="none"
                                    >
                                      <path
                                        d="M1 5L4.5 8.5L11 1.5"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  )}
                                </div>
                                <div style={styles.dayLabel}>{day}</div>
                              </>
                            ) : (
                              <>
                                <div style={{ width: "23px" }} />
                                <div style={styles.dayLabel}></div>
                              </>
                            )}

                            <div style={styles.timeSlotGroup}>
                              <div style={styles.timeSlotInputs}>
                                <TimePicker
                                  value={slot.start}
                                  onChange={(value) =>
                                    updateTimeSlot(day, slot.id, "start", value)
                                  }
                                  placeholder="Start time"
                                  error={hasError}
                                  availableTimes={getAvailableTimesForSlot(
                                    day,
                                    slot.id,
                                    "start",
                                  )}
                                />
                                <span style={{ color: "#6B7280" }}>to</span>
                                <TimePicker
                                  value={slot.end}
                                  onChange={(value) =>
                                    updateTimeSlot(day, slot.id, "end", value)
                                  }
                                  placeholder="End time"
                                  error={hasError}
                                  availableTimes={getAvailableTimesForSlot(
                                    day,
                                    slot.id,
                                    "end",
                                  )}
                                />
                              </div>
                              <button
                                type="button"
                                style={{
                                  ...styles.iconButton,
                                  ...(formData.availability[day].slots
                                    .length === 1
                                    ? styles.iconButtonDisabled
                                    : {}),
                                }}
                                onClick={() => removeTimeSlot(day, slot.id)}
                                title="Remove time slot"
                                disabled={
                                  formData.availability[day].slots.length === 1
                                }
                              >
                                <Trash2 size={18} />
                              </button>
                              {index ===
                                formData.availability[day].slots.length - 1 && (
                                <button
                                  type="button"
                                  style={styles.iconButton}
                                  onClick={() => addTimeSlot(day)}
                                  title="Add another time slot"
                                >
                                  <Plus size={18} />
                                </button>
                              )}
                            </div>
                          </div>
                          {hasError && (
                            <div style={styles.validationError}>
                              <AlertCircle size={12} />
                              <span>{slotError.message}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={styles.dayRow}>
                    <div
                      style={{
                        ...styles.checkbox,
                        ...(formData.availability[day].enabled
                          ? styles.checkboxChecked
                          : {}),
                      }}
                      onClick={() => toggleDay(day)}
                    >
                      {formData.availability[day].enabled && (
                        <svg
                          width="12"
                          height="10"
                          viewBox="0 0 12 10"
                          fill="none"
                        >
                          <path
                            d="M1 5L4.5 8.5L11 1.5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <div style={styles.dayLabel}>{day}</div>
                    <span style={styles.unavailableText}>Unavailable</span>
                    <button
                      type="button"
                      style={styles.iconButton}
                      onClick={() => addTimeSlot(day)}
                      title="Add time slot"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                )}
              </div>
            ))}

            <FormGroup label="Timeslot Duration" marginTop="24px">
              <Dropdown
                trigger={
                  <div style={styles.durationDropdownTrigger}>
                    <span>{formData.timeslotDuration}</span>
                    <ChevronDown size={18} color="#6B7280" />
                  </div>
                }
                minWidth="200px"
              >
                {timeslotOptions.map((option) => (
                  <DropdownItem
                    key={option}
                    onClick={() =>
                      handleInputChange("timeslotDuration", option)
                    }
                    variant={
                      option === formData.timeslotDuration
                        ? "selected"
                        : "default"
                    }
                  >
                    {option}
                  </DropdownItem>
                ))}
              </Dropdown>
            </FormGroup>
          </div>
        </div>

        <div style={styles.footer}>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={!hasChanges || saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !hasChanges || hasValidationErrors()}
          >
            {saving ? (
              <>
                <Loader2
                  size={16}
                  style={{ animation: "spin 1s linear infinite" }}
                />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
